import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PencilLine,
  Trash2,
  KeyRound,
  Search,
  X,
  PlusCircle,
  Users
} from 'lucide-react';
import AddUserModal from './AddUserModal';
import ChangePasswordModal from './ChangePasswordModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import { authservice } from '../../../../components/api/authservice';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  roleID: string;
  roleName?: string;
}

interface UserManagementModalProps {
  onClose: () => void;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({ onClose }) => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const refreshUserList = async () => {
    setLoading(true);
    try {
      const res = await authservice.getUsers();
      setUsers(res || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUserList();
  }, []);

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await authservice.deleteUser(userToDelete.id, userToDelete.tenantId);
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
    } catch (err) {
      console.error('Failed to delete user:', err);
    } finally {
      setUserToDelete(null);
    }
  };

  const handlePasswordUpdate = async (newPassword: string) => {
    if (!selectedUserForPassword) return;
    try {
      await authservice.updatePassword(selectedUserForPassword.id, newPassword, selectedUserForPassword.tenantId);
    } catch (err) {
      console.error('Failed to update password:', err);
    } finally {
      setSelectedUserForPassword(null);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(search.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-6 font-sans">
      <div className="bg-neutral-900 text-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden border border-neutral-800 transition-colors duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-900/30 flex items-center justify-center">
              <Users className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold tracking-tight">Users Management</h3>
              <p className="text-xs text-gray-400 font-medium">Add, edit, or remove system users and manage security.</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-neutral-800 transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 mb-6">
            <button
              onClick={() => { setEditUserId(null); setShowUserModal(true); }}
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl text-sm font-bold shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
            >
              <PlusCircle className="w-5 h-5" /> Add New User
            </button>

            <div className="relative group flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
              <input
                type="text"
                placeholder="Search by name or email address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-neutral-800 border border-neutral-700 text-white pl-10 pr-4 py-3 rounded-2xl text-sm outline-none ring-1 ring-transparent focus:ring-indigo-500 transition-all placeholder:text-gray-400"
              />
            </div>
          </div>

          <div className="overflow-hidden border border-neutral-800 rounded-2xl bg-black/10">
            <div className="overflow-x-auto max-h-[50vh] custom-scrollbar">
              <table className="w-full text-left text-sm text-gray-300">
                <thead className="bg-neutral-950 text-gray-400 uppercase text-[10px] font-black tracking-widest sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-4">User Information</th>
                    <th className="px-6 py-4">Role / Permissions</th>
                    <th className="px-6 py-4 text-right">Operational Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {loading ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-20 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-gray-500 font-medium">Fetching users list...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="group hover:bg-neutral-800 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-bold">{user.firstName} {user.lastName}</span>
                            <span className="text-xs text-white opacity-60">{user.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-neutral-800 text-gray-300 border border-neutral-700">
                            {user.roleName ?? user.roleID}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right cursor-default" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => { setEditUserId(user.id); setShowUserModal(true); }}
                              className="p-2 rounded-xl text-white opacity-40 hover:opacity-100 hover:text-indigo-400 hover:bg-neutral-800 transition-all"
                              title="Edit User"
                            >
                              <PencilLine className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setSelectedUserForPassword(user)}
                              className="p-2 rounded-xl text-white opacity-40 hover:opacity-100 hover:text-amber-400 hover:bg-neutral-800 transition-all"
                              title="Change Password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setUserToDelete(user)}
                              className="p-2 rounded-xl text-white opacity-40 hover:opacity-100 hover:text-red-400 hover:bg-neutral-800 transition-all"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-center py-20 text-gray-400 italic font-medium">
                        No users found matching your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-black/20 border-t border-neutral-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-8 py-2.5 rounded-2xl bg-white text-gray-900 font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-xl shadow-white/10"
          >
            Finished
          </button>
        </div>
      </div>

      {showUserModal && (
        <AddUserModal
          onClose={() => setShowUserModal(false)}
          onSubmit={() => {
            setShowUserModal(false);
            refreshUserList();
          }}
          userId={editUserId || undefined}
        />
      )}

      {selectedUserForPassword && (
        <ChangePasswordModal
          email={selectedUserForPassword.email}
          onClose={() => setSelectedUserForPassword(null)}
          onSubmit={handlePasswordUpdate}
        />
      )}

      {userToDelete && (
        <ConfirmDialog
          message={`Are you sure you want to permanently delete user "${userToDelete.email}"? This action cannot be undone.`}
          onYes={handleDeleteUser}
          onNo={() => setUserToDelete(null)}
          onCancel={() => setUserToDelete(null)}
        />
      )}
    </div>,
    document.body
  );
};

export default UserManagementModal;
